/**
 * Filling — the operation this app could not do.
 *
 * Two behaviours behind one tool, and the switch between them is explicit
 * rather than a heuristic guessing which was meant.
 *
 *   **Fill a face.** Every texel of one region. Ours, and no general canvas
 *   library can express it: "fill the left sleeve" is meaningless without the
 *   region map. It is also the first thing anyone does when making a garment,
 *   which is why its absence mattered — a base colour was reachable only by
 *   brushing the whole panel by hand or by tinting a fabric, and tint is paid.
 *
 *   **Flood fill.** The classic: spread from a seed across pixels close enough
 *   to the colour under it.
 *
 * The arithmetic lives here, away from the canvas, because both of the ways
 * this can be wrong are arithmetic — a flood that escapes its region, and a
 * tolerance that either leaves a halo or swallows the artwork.
 */
import type { Rect } from "../../shared/format";

/** A rectangle of the template, in texels. */
export interface FillRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface FloodOptions {
  /** RGBA, row-major, `width * height * 4`. */
  data: Uint8ClampedArray;
  width: number;
  height: number;
  /** Where the user tapped. */
  x: number;
  y: number;
  /**
   * 0–255. Distance from the seed colour that still counts as the same.
   *
   * Compared as a threshold rather than by equality because artwork is
   * antialiased: an exact match stops at the first blended pixel and leaves a
   * one-texel halo of the old colour around everything it filled, which is the
   * first thing every implementation guide warns about.
   */
  tolerance: number;
  /**
   * The region the fill may not leave.
   *
   * Not optional, and this is the decision that matters most here. The template
   * is a UV atlas: the 54.9% between the faces is dead space, and on a fresh
   * layer every face is equally transparent. An unbounded flood from an empty
   * chest is therefore connected to the sleeves, the back, and the gaps — one
   * tap would fill the entire garment. Bounding it to the face under the seed
   * makes the tool predictable, and "the whole panel" is what the other mode is
   * for.
   */
  bounds: Rect | FillRect;
  /**
   * How far past `tolerance` a texel may be and still be painted, partially.
   *
   * Tolerance alone cannot close the gap. Artwork is antialiased, so every edge
   * is a one- or two-texel ramp between two colours; the flood stops at the
   * first texel too far from the seed, and those ramp texels are left holding
   * the *old* colour. The result is a fringe of the previous colour around
   * everything the fill touched — the complaint every flood implementation
   * eventually gets.
   *
   * Raising `tolerance` instead is the wrong lever: it does not soften the
   * edge, it moves it, and far enough to swallow the fringe is far enough to
   * swallow the artwork the fringe belongs to.
   *
   * So the ramp texels are painted at an alpha proportional to how much they
   * already resembled the seed, and — the part that matters — they are **never
   * traversed**. A soft gradient is a corridor: flooding through one at partial
   * strength would walk the fill straight out of the shape, which is the bug
   * that feathering naively usually introduces.
   *
   * 0 disables it, and the coverage is then exactly the mask.
   */
  feather?: number;
}

export interface FloodResult {
  /** One byte per texel of `rect`: 1 to fill, 0 to leave. */
  mask: Uint8Array;
  /**
   * One byte per texel of `rect`: how strongly to paint it, 0–255.
   *
   * 255 wherever `mask` is 1, a ramp on the feathered fringe just outside it,
   * 0 elsewhere. Paint through this rather than the mask; with `feather` off
   * the two agree exactly.
   */
  coverage: Uint8Array;
  /** Where the mask sits in the template. */
  rect: FillRect;
}

/** Squared distance between two RGBA samples, alpha included. */
function difference(d: Uint8ClampedArray, a: number, b: number): number {
  const dr = (d[a] as number) - (d[b] as number);
  const dg = (d[a + 1] as number) - (d[b + 1] as number);
  const db = (d[a + 2] as number) - (d[b + 2] as number);
  const da = (d[a + 3] as number) - (d[b + 3] as number);
  return dr * dr + dg * dg + db * db + da * da;
}

/**
 * The same metric the flood compares against `tolerance`, but unsquared.
 *
 * `matches` tests `difference <= tolerance² × 4`, so the distance that
 * corresponds to is `√difference / 2`. Written once, here, because a feather
 * ramp measured on a different scale from the threshold it extends would be
 * wrong in a way that looks like a tuning problem rather than an arithmetic
 * one.
 */
function distance(d: Uint8ClampedArray, a: number, b: number): number {
  return Math.sqrt(difference(d, a, b)) / 2;
}

/**
 * Which texels a flood from this seed reaches.
 *
 * Scanline with an explicit stack. The recursive version is the one every
 * tutorial shows and it overflows on any real area — a 128×128 face is sixteen
 * thousand frames deep in the worst case.
 *
 * Returns null when the seed is outside its own bounds, which a tap in the
 * dead space between faces produces routinely.
 */
export function floodMask(opts: FloodOptions): FloodResult | null {
  const { data, width, height, tolerance } = opts;
  const b = opts.bounds;
  const x0 = Math.max(0, Math.floor(b.x));
  const y0 = Math.max(0, Math.floor(b.y));
  const x1 = Math.min(width - 1, Math.floor(b.x + b.w - 1));
  const y1 = Math.min(height - 1, Math.floor(b.y + b.h - 1));

  const sx = Math.floor(opts.x);
  const sy = Math.floor(opts.y);
  if (sx < x0 || sx > x1 || sy < y0 || sy > y1) return null;

  const w = x1 - x0 + 1;
  const h = y1 - y0 + 1;
  if (w <= 0 || h <= 0) return null;

  const mask = new Uint8Array(w * h);
  const seed = (sy * width + sx) * 4;
  // Squared, so the comparison below needs no square root. Four channels, so
  // the ceiling is 4 × 255².
  const limit = tolerance * tolerance * 4;

  const matches = (x: number, y: number) => difference(data, (y * width + x) * 4, seed) <= limit;

  /** Rows still to examine, as [x, y] pairs. Flat, to avoid an object per push. */
  const stack: number[] = [sx, sy];

  while (stack.length > 0) {
    const y = stack.pop() as number;
    const x = stack.pop() as number;
    const row = (y - y0) * w;
    if (mask[row + (x - x0)] === 1) continue;

    // Walk left and right to the ends of this run.
    let left = x;
    while (left > x0 && mask[row + (left - 1 - x0)] !== 1 && matches(left - 1, y)) left--;
    let right = x;
    while (right < x1 && mask[row + (right + 1 - x0)] !== 1 && matches(right + 1, y)) right++;
    if (!matches(x, y)) continue;

    for (let i = left; i <= right; i++) mask[row + (i - x0)] = 1;

    /*
      Seed the neighbouring rows once per *run* rather than once per texel.

      Pushing every pixel above and below is the difference between a stack
      thousands deep and one a handful deep, on exactly the fills that matter —
      a whole panel of flat colour.
    */
    for (const ny of [y - 1, y + 1]) {
      if (ny < y0 || ny > y1) continue;
      const nrow = (ny - y0) * w;
      let run = false;
      for (let i = left; i <= right; i++) {
        const filled = mask[nrow + (i - x0)] === 1;
        if (!filled && matches(i, ny)) {
          if (!run) {
            stack.push(i, ny);
            run = true;
          }
        } else {
          run = false;
        }
      }
    }
  }

  const coverage = new Uint8Array(w * h);
  for (let i = 0; i < mask.length; i++) coverage[i] = mask[i] === 1 ? 255 : 0;

  const feather = opts.feather ?? 0;
  if (feather > 0) {
    /*
      One ramp wide, and only where it touches what was filled.

      Every texel the flood declined is a candidate on colour alone — including
      the entire far side of a boundary, which is nowhere near the fill and must
      not be tinted. Requiring a filled 4-neighbour is what makes this an edge
      treatment rather than a wash, and it is why this runs as a pass over the
      finished mask instead of during the walk: mid-walk, "is this next to the
      filled area" has no stable answer yet.
    */
    for (let yy = y0; yy <= y1; yy++) {
      const row = (yy - y0) * w;
      for (let xx = x0; xx <= x1; xx++) {
        const idx = row + (xx - x0);
        if (mask[idx] === 1) continue;
        const touches =
          (xx > x0 && mask[idx - 1] === 1) ||
          (xx < x1 && mask[idx + 1] === 1) ||
          (yy > y0 && mask[idx - w] === 1) ||
          (yy < y1 && mask[idx + w] === 1);
        if (!touches) continue;

        // 0 at the tolerance edge, 1 at the far end of the feather band.
        const t = (distance(data, (yy * width + xx) * 4, seed) - tolerance) / feather;
        if (t >= 1) continue;
        coverage[idx] = Math.round(255 * (1 - Math.max(0, t)));
      }
    }
  }

  return { mask, coverage, rect: { x: x0, y: y0, w, h } };
}

/**
 * The rectangle a face fill should cover.
 *
 * The region itself, grown by the gutter. The gutter carries no geometry and is
 * never sampled by the body, but the export pipeline reads it when it dilates
 * edges — a face filled exactly to its own rim frays by a texel at distance,
 * which is the whole problem the edge work exists to solve.
 */
export function faceFillRect(region: Rect, gutter: number): FillRect {
  return {
    x: Math.max(0, region.x - gutter),
    y: Math.max(0, region.y - gutter),
    w: region.w + gutter * 2,
    h: region.h + gutter * 2,
  };
}

/**
 * Every face of the same body part as this one.
 *
 * Taken from the region id's prefix — `leftLimb.front` and `leftLimb.back` are
 * the same sleeve; `torso.up` is not. That is finer than `groupFor`, which puts
 * both arms in one "arms" group: filling one sleeve should not fill the other,
 * because a garment with different sleeves is a thing people make on purpose.
 *
 * The prefix is the whole rule. It looks fragile and is not: the ids are
 * generated from the measured template and every one of them is
 * `<part>.<face>`, which is the same convention `seam.ts` walks strips with.
 */
export function partFaces<T extends string>(region: T, all: readonly T[]): T[] {
  const part = region.split(".")[0];
  if (!part) return [region];
  return all.filter((id) => id.split(".")[0] === part);
}

/**
 * The smallest rectangle containing all of them.
 *
 * The fill writes face by face, but undo and the recomposite want one rect —
 * pushing a history entry per face would make "fill this sleeve" four presses
 * of undo to take back.
 */
export function unionRect(rects: readonly FillRect[]): FillRect | null {
  if (rects.length === 0) return null;
  let x0 = Number.POSITIVE_INFINITY;
  let y0 = Number.POSITIVE_INFINITY;
  let x1 = Number.NEGATIVE_INFINITY;
  let y1 = Number.NEGATIVE_INFINITY;
  for (const r of rects) {
    if (r.x < x0) x0 = r.x;
    if (r.y < y0) y0 = r.y;
    if (r.x + r.w > x1) x1 = r.x + r.w;
    if (r.y + r.h > y1) y1 = r.y + r.h;
  }
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}
